function alphaPercent(hex) {
  return Math.round((parseInt(hex, 16) / 255) * 1000) / 10;
}

export function alpha(color, hex) {
  return `color-mix(in srgb, ${color} ${alphaPercent(hex)}%, transparent)`;
}

export function borderAlpha(color, hex, width = "1px") {
  return `${width} solid ${alpha(color, hex)}`;
}

export function Glass({ children, style = {} }) {
  return (
    <div
      style={{
        background: "color-mix(in srgb, var(--color-kin-white) 58%, transparent)",
        border: "1px solid color-mix(in srgb, var(--color-kin-white-pure) 35%, transparent)",
        borderRadius: 20,
        backdropFilter: "blur(24px) saturate(150%)",
        boxShadow:
          "0 4px 16px -4px rgba(0,0,0,0.04), 0 2px 4px -2px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.7)",
        transition: "all 0.4s cubic-bezier(0.25, 1, 0.3, 1)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function GlassStrong({ children, style = {} }) {
  return (
    <div
      style={{
        background: "color-mix(in srgb, var(--color-kin-white) 76%, transparent)",
        border: "1px solid color-mix(in srgb, var(--color-kin-white-pure) 42%, transparent)",
        borderRadius: 20,
        backdropFilter: "blur(32px) saturate(180%)",
        boxShadow:
          "0 12px 32px -8px rgba(0,0,0,0.06), 0 4px 12px -4px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.9)",
        transition: "all 0.4s cubic-bezier(0.25, 1, 0.3, 1)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function PillChip({ children, active = false, color = "var(--color-kin-green-soft)" }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 11px",
        borderRadius: 20,
        background: active
          ? `color-mix(in srgb, ${color} 10%, transparent)`
          : "color-mix(in srgb, var(--color-kin-white) 66%, transparent)",
        border: `1px solid ${active ? `color-mix(in srgb, ${color} 26%, transparent)` : "rgba(28,30,29,0.09)"}`,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.05em",
        color: active ? color : "var(--color-kin-muted)",
        fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

export function Dot({ color, size = 7, style = {} }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
        ...style,
      }}
    />
  );
}

export function ScreenLabel({ children, style = {} }) {
  return (
    <p
      style={{
        fontSize: 8,
        fontWeight: 700,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: "var(--color-kin-muted)",
        fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
        ...style,
      }}
    >
      {children}
    </p>
  );
}

export function DisplayText({
  children,
  size = 22,
  color = "var(--color-kin-black)",
  lineHeight = 1.05,
}) {
  return (
    <p
      style={{
        fontSize: size,
        fontWeight: 700,
        color,
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        lineHeight,
      }}
    >
      {children}
    </p>
  );
}

export function BodyText({
  children,
  size = 11,
  color = "var(--color-kin-body)",
  weight = 600,
  style = {},
}) {
  return (
    <p
      style={{
        fontSize: size,
        fontWeight: weight,
        color,
        fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
        lineHeight: 1.5,
        ...style,
      }}
    >
      {children}
    </p>
  );
}

/**
 * SkillSigil — compact attribution tag showing a skill is speaking.
 * Diamond glyph + skill name in a tinted capsule. The diamond distinguishes
 * skill reactions from all other UI elements (which use dots). Use anywhere
 * a skill "peeks through" — home feed, data entries, notifications.
 */
export function SkillSigil({ name, color, style = {} }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px 3px 7px",
        borderRadius: 12,
        background: alpha(color, "12"),
        border: borderAlpha(color, "22"),
        ...style,
      }}
    >
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: 3,
          border: borderAlpha(color, "40", "1.5px"),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: 1.5,
            background: color,
          }}
        />
      </div>
      <span
        style={{
          fontSize: 8,
          fontWeight: 800,
          color,
          fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
          letterSpacing: "0.02em",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
    </div>
  );
}

export function BackNav({ label = "Back", color = "var(--color-kin-green-soft)" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}>
      <svg
        width="8"
        height="12"
        viewBox="0 0 8 12"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6.5 1l-5 5 5 5" />
      </svg>
      <BodyText size={10} color={color} weight={600}>
        {label}
      </BodyText>
    </div>
  );
}

export function Flag({ type }) {
  if (!type) return null;
  const isHigh = type === "HIGH" || type === "CRIT HIGH";
  const color = isHigh ? "var(--color-kin-rose)" : "var(--color-kin-gold-dark)";
  const glyph = isHigh ? "!" : "?";
  return (
    <div
      style={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: alpha(color, "18"),
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontSize: 9,
          fontWeight: 800,
          color,
          fontFamily: "'Manrope', system-ui",
          lineHeight: 1,
        }}
      >
        {glyph}
      </span>
    </div>
  );
}

export function Chevron({ color = "var(--color-kin-olive-light)" }) {
  return (
    <svg
      width="8"
      height="12"
      viewBox="0 0 8 12"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M1.5 1l5 5-5 5" />
    </svg>
  );
}
